import React, { useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  List,
  Datagrid,
  TextField,
  DeleteWithConfirmButton,
  Create,
  SimpleForm,
  Edit,
  SelectInput,
  ReferenceArrayInput,
  DateField,
  useNotify,
  useRedirect,
  SimpleShowLayout,
  useRecordContext,
  ReferenceField,
  TextInput,
  Button,
} from 'react-admin'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Button as MuiButton,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { generateToken } from '../utils/firebase'
import { showNotification } from '../components/NotificationToast'
  
import { TemplateVariables, sendTest } from '..'

const inputFields = [
  { name: 'title', required: true, multiline: false },
  { name: 'body', required: false, multiline: true },
]

const SendTestButton = ({ customUser = false }) => {
  const [open, setOpen] = useState(false)
  const [deviceToken, setDeviceToken] = useState('')
  const [myToken, setMyToken] = useState('')
  const [userId, setUserId] = useState('')
  const [tokenCopied, setTokenCopied] = useState(false)
  const record = useRecordContext()
  const notify = useNotify()

  const handleClick = (event) => {
    event.stopPropagation()
    setOpen(true)
    generateToken().then((token) => {
      setMyToken(token)
    })
  }

  const handleClose = () => {
    setOpen(false)
    setDeviceToken('')
    setUserId('')
    setTokenCopied(false)
  }

  const handleSend = (event) => {
    event.stopPropagation()

    const data = customUser
      ? {
          templateName: record.name,
          ...(deviceToken && { deviceToken }),
          ...(userId && { userId }),
        }
      : {
          templateName: record.name,
        }

    sendTest('PushTemplate', { data })
      .then(() => {
        notify(`Test push '${record.name}' sent successfully`, { type: 'info' })
        handleClose()
      })
      .catch((error) => {
        notify(`Error sending test push: ${error.message}`, { type: 'warning' }) //
      })
  }

  const copyTokenToClipboard = () => {
    navigator.clipboard.writeText(myToken)
    setTokenCopied(true)
    setTimeout(() => setTokenCopied(false), 2000)
  }

  const isSendDisabled = customUser && !deviceToken && !userId

  return (
    <>
      <Button
        startIcon={<SendIcon />}
        label="Send Test"
        onClick={customUser ? handleClick : handleSend}
        sx={{ zIndex: 9999 }}
      />
      <Dialog open={open} onClose={handleClose} onClick={(event) => event.stopPropagation()}>
        <DialogTitle>Send Test push</DialogTitle>
        <DialogContent>
          <MuiTextField
            autoFocus
            sx={{ mt: 2 }}
            margin="dense"
            id="deviceToken"
            label="Device Token"
            type="string"
            fullWidth
            variant="outlined"
            value={deviceToken}
            onChange={(e) => {
              setDeviceToken(e.target.value)
            }}
          />
          <MuiTextField
            margin="dense"
            id="userId"
            label="User Id"
            type="string"
            fullWidth
            variant="outlined"
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value)
            }}
            sx={{ mt: 2 }}
          />
          <Typography mt={2} color={isSendDisabled ? 'error' : 'textSecondary'}>
            {isSendDisabled
              ? 'You must enter either a device token or a user ID.'
              : 'Device token or user ID will be used to send the test.'}
          </Typography>

          <Box
            mt={3}
            sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 10 }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Your browser device token:
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  width: '100%',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                }}
              >
                <Box
                  sx={{
                    padding: '18px 10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    flexGrow: 1,
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    '&::-webkit-scrollbar': {
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(0,0,0,0.15)',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: 'rgba(0,0,0,0.25)',
                    },
                  }}
                >
                  {myToken || 'No token generated yet'}
                </Box>
                
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: '48px',
                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                    borderLeft: '1px solid rgba(0, 0, 0, 0.23)',
                  }}
                >
                  <Tooltip title={tokenCopied ? 'Copied!' : 'Copy to clipboard'} placement="top">
                    <IconButton
                      size="small"
                      onClick={copyTokenToClipboard}
                      sx={{
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        },
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '0px 23px 23px 16px' }}>
          <MuiButton onClick={handleClose}>Cancel</MuiButton>
          <MuiButton
            onClick={handleSend}
            disabled={isSendDisabled}
            variant="contained"
            startIcon={<SendIcon />}
          >
            Send
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const PushTemplateList = ({ ...props }) => (
  <List>
    <Datagrid rowClick="edit">
      <TextField source="name" label="Template Name" />
      <TextField source="type" label="Type" />
      <ReferenceField source="createdBy" reference="users" />
      <DateField source="createdAt" label="Created At" showTime />
      <ReferenceField source="updatedBy" reference="users" />
      <DateField source="updatedAt" label="Updated At" showTime />
      <SendTestButton customUser={true} {...props} />
      <DeleteWithConfirmButton />
    </Datagrid>
  </List>
)

const TemplateFieldArea = () => {
  const [activeInput, setActiveInput] = useState(null)
  const [inputValues, setInputValues] = useState(
    Object.fromEntries(inputFields.map(({ name }) => [name, '']))
  )

  const inputRefs = useRef(Object.fromEntries(inputFields.map(({ name }) => [name, null])))

  const { control, setValue } = useFormContext()
  const templateName = useWatch({ control, name: 'name' })

  const handleVariableInsert = (variable) => {
    if (!activeInput) return

    const insertText = `{{${variable}}}`
    const input = inputRefs.current[activeInput]
    const currentValue = inputValues[activeInput] || ''

    if (input) {
      const start = input.selectionStart ?? currentValue.length
      const end = input.selectionEnd ?? currentValue.length

      const newValue =
        currentValue.substring(0, start) + insertText + currentValue.substring(end)

      setInputValues((prev) => ({ ...prev, [activeInput]: newValue }))
      setValue(activeInput, newValue)

      setTimeout(() => {
        input.focus()
        input.setSelectionRange(start + insertText.length, start + insertText.length)
      }, 0)
    }
  }

  const handleInputChange = (name, value) => {
    setInputValues((prev) => ({ ...prev, [name]: value }))
    setValue(name, value)
  }

  const registerRef = (name) => (ref) => {
    inputRefs.current[name] = ref
  }

  return (
    <>
      <ReferenceArrayInput source="name" reference="servicePushTemplates" label="Template name">
        <SelectInput source="name" label="Template name" />
      </ReferenceArrayInput>
      <SelectInput
        source="type"
        defaultValue={'ANDROID'}
        choices={[
          { id: 'ANDROID', name: 'ANDROID' },
          { id: 'APPLE', name: 'APPLE' },
          { id: 'GENERAL', name: 'GENERAL' },
        ]}
      />

      <TemplateVariables templateName={templateName} onVariableClick={handleVariableInsert} />

      {inputFields.map(({ name, required, multiline }) => (
        <TextInput
          key={name}
          source={name}
          label={name[0].toUpperCase() + name.slice(1)}
          value={inputValues[name]}
          onFocus={() => setActiveInput(name)}
          onChange={(e) => handleInputChange(name, e.target.value)}
          disabled={!templateName}
          fullWidth
          multiline={multiline}
          required={required}
          InputProps={{ inputRef: registerRef(name) }}
        />
      ))}

      {!templateName && (
        <Typography mb={2}>Please select template to activate input.</Typography>
      )}
    </>
  )
}

export const PushTemplateCreate = ({ resourceName, ...props }) => {
  const redirect = useRedirect()
  const notify = useNotify()

  const onSuccess = (data) => {
    notify(`Template "${data.name}" created successfully`, { type: 'info' })
    if (resourceName) redirect('list', resourceName)
  }

  return (
    <Create mutationOptions={{ onSuccess }}>
      <SimpleForm>
        <TemplateFieldArea />
      </SimpleForm>
    </Create>
  )
}

const TemplateEditFieldArea = () => {
  const { control, setValue } = useFormContext()

  const templateName = useWatch({ control, name: 'name' })

  const watchedValues = {
    title: useWatch({ control, name: 'title' }),
    body: useWatch({ control, name: 'body' }),
  }

  const [activeInput, setActiveInput] = useState(null)
  const [inputValues, setInputValues] = useState(
    Object.fromEntries(inputFields.map(({ name }) => [name, '']))
  )

  const inputRefs = useRef(Object.fromEntries(inputFields.map(({ name }) => [name, null])))

  useEffect(() => {
    const shouldUpdate = inputFields.some(
      ({ name }) => watchedValues[name] && !inputValues[name]
    )

    if (shouldUpdate) {
      const updated = {}
      inputFields.forEach(({ name }) => {
        if (watchedValues[name] && !inputValues[name]) {
          updated[name] = watchedValues[name]
        }
      })
      setInputValues((prev) => ({ ...prev, ...updated }))
    }
  }, [watchedValues])

  const handleVariableInsert = (variable) => {
    if (!activeInput) return

    const insertText = `{{${variable}}}`
    const input = inputRefs.current[activeInput]
    const currentValue = inputValues[activeInput] || ''

    if (input) {
      const start = input.selectionStart ?? currentValue.length
      const end = input.selectionEnd ?? currentValue.length

      const newValue =
        currentValue.substring(0, start) + insertText + currentValue.substring(end)

      setInputValues((prev) => ({ ...prev, [activeInput]: newValue }))
      setValue(activeInput, newValue)

      setTimeout(() => {
        input.focus()
        input.setSelectionRange(start + insertText.length, start + insertText.length)
      }, 0)
    }
  }

  const handleInputChange = (name, value) => {
    setInputValues((prev) => ({ ...prev, [name]: value }))
    setValue(name, value)
  }

  const registerRef = (name) => (ref) => {
    inputRefs.current[name] = ref
  }

  return (
    <>
      <SelectInput
        source="type"
        defaultValue={'ANDROID'}
        choices={[
          { id: 'ANDROID', name: 'ANDROID' },
          { id: 'APPLE', name: 'APPLE' },
          { id: 'GENERAL', name: 'GENERAL' },
        ]}
      />
      <TemplateVariables templateName={templateName} onVariableClick={handleVariableInsert} />

      {inputFields.map(({ name, required, multiline }) => (
        <TextInput
          key={name}
          source={name}
          label={name[0].toUpperCase() + name.slice(1)}
          value={inputValues[name]}
          onFocus={() => setActiveInput(name)}
          onChange={(e) => handleInputChange(name, e.target.value)}
          disabled={!templateName}
          fullWidth
          multiline={multiline}
          required={required}
          InputProps={{ inputRef: registerRef(name) }}
        />
      ))}

      {!templateName && (
        <Typography mb={2}>Please select template to activate input.</Typography>
      )}
    </>
  )
}

export const PushTemplateEdit = () => {
  const [initialTemplateData, setInitialTemplateData] = useState({})

  const InitialDataSetter = () => {
    const record = useRecordContext()
    if (!initialTemplateData.name) {
      setInitialTemplateData(record)
    }
  }

  const TemplateTitle = () => {
    return <span>Template {initialTemplateData ? `"${initialTemplateData.name}"` : ''}</span>
  }

  return (
    <Edit title={<TemplateTitle />}>
      <SimpleShowLayout>
        <InitialDataSetter />
        <TextField source="name" label="Template name" />
      </SimpleShowLayout>
      <SimpleForm>
        <TemplateEditFieldArea />
      </SimpleForm>
    </Edit>
  )
}
